import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, startGoogleLogin } from "../lib/auth";
import { LegalLinks } from "../components/Legal";
import { toast } from "sonner";
import { AUTH } from "../constants/testIds";
import { Mail, Lock, Eye, EyeOff, Target, Brain, TrendingUp, Sparkles } from "lucide-react";

// YZMatematik giriş ekranı — referanslar:
//  * watermark.png            → temiz öğrenci fotoğrafı (arka plan, metinsiz)
//  * beklenen-web.png         → web/tablet nihai düzen (sol foto panel + sağ beyaz form)
//  * beklenen-mobil.png       → mobil nihai düzen (tam ekran foto + koyu form akışı)
// Marka: lacivert #061226/#0A1B33, altın #F5B301, mavi #4DA3FF.
// TEK LoginForm render edilir (testid'ler DOM'da bir kez); web/mobil farkları
// yalnızca responsive class'larla uygulanır.
const FEATURES = [
  { icon: Target, label: "10 soruluk oturumlar" },
  { icon: Brain, label: "Sana özel matematik haritası" },
  { icon: TrendingUp, label: "Öğrenmeni hızlandıran akıllı seçim" },
  { icon: Sparkles, label: "Her gün bir adım ileri" },
];

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.7-.4-3.9z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.7-.4-3.9z"/>
    </svg>
  );
}

function FeatureRow({ light = false }) {
  return (
    <div className="grid grid-cols-4 gap-2">
      {FEATURES.map((f) => (
        <div key={f.label} className="flex flex-col items-center text-center gap-1.5">
          <f.icon size={24} strokeWidth={1.8} className={light ? "text-[#0A1B33]" : "text-[#4DA3FF]"} />
          <span className={`text-[11px] leading-snug ${light ? "text-[#0A1B33]" : "text-white/80"}`}>{f.label}</span>
        </div>
      ))}
    </div>
  );
}

const Slogan = ({ light = false }) => (
  <p className={`text-sm italic ${light ? "text-[#D9A400]" : "text-[#F5B301]"}`}>
    "Matematiği ezberlemek yerine anlayarak öğren!"
  </p>
);

const LogoTitle = ({ testId, taglineId, dark = true, size = "text-3xl", logoSize = "h-20 w-20" }) => (
  <div className="flex flex-col items-center text-center">
    <img src="/brand/yz-icon.png" alt="YZMatematik"
         className={`${logoSize} rounded-full ring-2 ring-[#F5B301]/70 shadow-[0_0_50px_rgba(245,179,1,0.3)]`} />
    <div className={`mt-4 font-display ${size} tracking-tight`} data-testid={testId}>
      <span className={dark ? "text-white" : "text-[#0A1B33]"}>YZ</span>{" "}
      <span className="text-[#F5B301]">Matematik</span>
    </div>
    <div className={`text-sm mt-1.5 ${dark ? "text-[#7FB8FF]" : "text-slate-500"}`} data-testid={taglineId}>
      Yapay zekâ destekli dijital matematik koçu
    </div>
  </div>
);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Hoş geldin!");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Giriş başarısız");
    } finally {
      setBusy(false);
    }
  };

  const google = async () => {
    try {
      await startGoogleLogin();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Google ile giriş başlatılamadı");
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:grid md:grid-cols-2 bg-[#061226]">
      {/* Mobil için tam ekran watermark arka planı (beklenen-mobil.png) */}
      <img src="/brand/watermark.png" alt="" aria-hidden="true"
           className="md:hidden fixed inset-0 w-full h-full object-cover"
           style={{ objectPosition: "center 30%" }} />
      <div className="md:hidden fixed inset-0"
           style={{ background: "linear-gradient(180deg, rgba(6,18,38,0.72) 0%, rgba(6,18,38,0.30) 32%, rgba(6,18,38,0.55) 55%, rgba(4,13,29,0.92) 100%)" }} />

      {/* ===== Sol marka paneli ===== */}
      <div className="relative overflow-hidden">
        {/* Web/tablet: panel arka planı olarak watermark (beklenen-web.png) */}
        <img src="/brand/watermark.png" alt="" aria-hidden="true"
             className="hidden md:block absolute inset-0 w-full h-full object-cover"
             style={{ objectPosition: "center 35%" }} />
        <div className="hidden md:block absolute inset-0"
             style={{ background: "linear-gradient(180deg, rgba(6,18,38,0.55) 0%, rgba(6,18,38,0.05) 40%, rgba(6,18,38,0.45) 100%)" }} />
        {/* Web/tablet marka bloğu — sol üst */}
        <div className="hidden md:block relative p-10">
          <img src="/brand/yz-icon.png" alt="YZMatematik"
               className="h-20 w-20 rounded-full ring-2 ring-[#F5B301]/70 shadow-[0_0_40px_rgba(245,179,1,0.25)]" />
          <div className="mt-6 font-display text-4xl tracking-tight" data-testid="login-brand-title-desktop">
            <span className="text-white">YZ</span> <span className="text-[#F5B301]">Matematik</span>
          </div>
          <div className="text-[#7FB8FF] mt-1.5" data-testid="login-brand-tagline-desktop">
            Yapay zekâ destekli dijital matematik koçu
          </div>
        </div>
        {/* Mobil marka bloğu — üst orta + fotoğrafın görünür kalması için boşluk */}
        <div className="md:hidden relative px-6" style={{ paddingTop: "max(2rem, env(safe-area-inset-top))" }}>
          <LogoTitle testId="login-brand-title" taglineId="login-brand-tagline" logoSize="h-24 w-24" />
          <div className="min-h-[9rem]" aria-hidden="true" />
        </div>
      </div>

      {/* ===== Form paneli — TEK form; mobilde koyu akış, web/tablette beyaz panel ===== */}
      <div className="relative md:bg-white flex flex-col flex-1">
        <div className="flex-1 flex items-center justify-center px-6 py-6 md:p-10">
          <div className="w-full max-w-sm yz-enter">
            {/* Web/tablet form üstü marka bloğu (beklenen-web.png sağ panel) */}
            <div className="hidden md:block mb-8">
              <LogoTitle dark={false} size="text-4xl" logoSize="h-16 w-16" />
            </div>
            <LoginForm email={email} setEmail={setEmail} password={password} setPassword={setPassword}
                       showPw={showPw} setShowPw={setShowPw} busy={busy} onSubmit={onSubmit} google={google} />
          </div>
        </div>
        {/* Web/tablet alt bölüm: özellik ikonları + slogan */}
        <div className="hidden md:block p-10 pt-0 space-y-4">
          <FeatureRow light />
          <div className="text-center"><Slogan light /></div>
          <LegalLinks />
        </div>
      </div>

      {/* ===== Mobil alt bölüm — özellik ikonları + slogan ===== */}
      <div className="md:hidden relative px-6 space-y-6"
           style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}>
        <div className="mt-2"><FeatureRow /></div>
        <div className="text-center"><Slogan /></div>
        <LegalLinks dark />
      </div>
    </div>
  );
}

function LoginForm({ email, setEmail, password, setPassword,
                     showPw, setShowPw, busy, onSubmit, google }) {
  // Mobil: koyu cam input; web/tablet: beyaz panelde açık input
  const inputCls =
    "bg-white/5 border border-white/15 text-white placeholder-white/45 focus:border-[#4DA3FF] focus:ring-2 focus:ring-[#4DA3FF]/25 " +
    "md:bg-white md:border-[#E2E8F0] md:text-[#0A1B33] md:placeholder-slate-400 md:focus:ring-[#4DA3FF]/20";
  const iconCls = "text-white/45 md:text-slate-400";
  return (
    <>
      <button type="button" onClick={google} data-testid={AUTH.googleBtn}
        className="w-full py-3.5 rounded-2xl font-medium flex items-center justify-center gap-2 bg-white text-[#0A1B33] transition-colors hover:bg-slate-100 md:hover:bg-white md:border md:border-[#D8E0EA] md:hover:border-[#4DA3FF]">
        <GoogleIcon /> Google ile devam et
      </button>
      <div className="flex items-center gap-2 my-4 text-xs text-white/55 md:text-slate-400">
        <span className="flex-1 h-px bg-white/20 md:bg-slate-200" />
        <span>veya</span>
        <span className="flex-1 h-px bg-white/20 md:bg-slate-200" />
      </div>
      <form onSubmit={onSubmit} className="space-y-3">
        <div className="relative">
          <Mail size={16} className={`absolute left-3.5 top-1/2 -translate-y-1/2 ${iconCls}`} />
          <input data-testid={AUTH.emailInput} type="email" required placeholder="E-posta"
            value={email} onChange={(e) => setEmail(e.target.value)}
            className={`w-full pl-10 pr-3 py-3 rounded-2xl outline-none transition-shadow ${inputCls}`} />
        </div>
        <div className="relative">
          <Lock size={16} className={`absolute left-3.5 top-1/2 -translate-y-1/2 ${iconCls}`} />
          <input data-testid={AUTH.passwordInput} type={showPw ? "text" : "password"} required placeholder="Şifre"
            value={password} onChange={(e) => setPassword(e.target.value)}
            className={`w-full pl-10 pr-11 py-3 rounded-2xl outline-none transition-shadow ${inputCls}`} />
          <button type="button" onClick={() => setShowPw(!showPw)} data-testid="login-toggle-password"
            aria-label={showPw ? "Şifreyi gizle" : "Şifreyi göster"}
            className={`absolute right-3 top-1/2 -translate-y-1/2 ${iconCls} hover:opacity-80`}>
            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <button disabled={busy} data-testid={AUTH.loginBtn}
          className="w-full py-3 rounded-2xl font-semibold text-[#061226] disabled:opacity-60 transition-transform hover:-translate-y-px"
          style={{ background: "linear-gradient(180deg,#FFC93C 0%,#F5B301 100%)", boxShadow: "0 6px 18px rgba(245,179,1,0.35)" }}>
          {busy ? "Giriş yapılıyor…" : "Giriş yap"}
        </button>
      </form>
      <div className="text-sm mt-5 text-white/70 md:text-slate-500">
        Hesabın yok mu?{" "}
        <Link data-testid={AUTH.toRegister} to="/kayit" className="text-[#4DA3FF] hover:underline">Kayıt ol</Link>
      </div>
    </>
  );
}
